<script setup lang="ts">
    import { Check, X } from 'lucide-vue-next';
    import type { OnboardingStep } from '@/types';
    import type { HTMLAttributes } from 'vue';
    import { cn } from '@/lib/utils';
    import { computed } from 'vue';

    type FormState = OnboardingStep & {
        status: 'completed' | 'rejected' | 'pending';
        selectedAcceptIndex?: string;
    };

    const props = defineProps<{
        step: OnboardingStep;
        formState?: FormState;
        class?: HTMLAttributes['class'];
        separator?: boolean;
    }>();

    // Use form state status if available, otherwise fall back to step status
    const status = computed(() => props.formState?.status ?? props.step.status);
</script>

<template>
    <div
        :class="
            cn(
                'relative flex w-full justify-center gap-2 px-2 transition-opacity duration-100 sm:px-4',
                props.class,
                {
                    'opacity-40': step.is_completed,
                },
            )
        "
    >
        <div
            class="border-primary flex size-4 items-center justify-center rounded-full border-2 text-white transition-all duration-100 sm:size-8"
            :class="{
                'bg-primary': status === 'completed',
                'bg-destructive !border-destructive': formState?.status === 'rejected',
            }"
        >
            <Transition
                name="icon"
                mode="out-in"
            >
                <Check
                    v-if="status === 'completed'"
                    class="size-4"
                />
                <X
                    v-else-if="formState?.status === 'rejected'"
                    class="size-4"
                />
                <span
                    v-else
                    class="size-4"
                />
            </Transition>
        </div>
        <div
            v-if="separator"
            class="bg-primary absolute top-2 right-[calc(-50%+8px)] left-[calc(50%+18px)] block h-0.5 shrink-0 rounded-full transition-all duration-100 sm:top-4 sm:right-[calc(-50%+16px)] sm:left-[calc(50%+24px)]"
        />
        <span
            class="text-primary absolute top-full left-0 mt-2 w-full text-center text-[10px] font-medium whitespace-nowrap transition-all duration-100 sm:text-sm"
            :class="{
                '!text-destructive': formState?.status === 'rejected',
            }"
        >
            {{ step.title }}
        </span>
    </div>
</template>

<style scoped>
    .icon-enter-active,
    .icon-leave-active {
        transition: all 0.1s ease;
    }

    .icon-enter-from,
    .icon-leave-to {
        opacity: 0;
        transform: scale(0.8);
    }
</style>
